import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Tabs,
  Tab,
  IconButton,
  Snackbar,
  Alert,
  Button,
  CircularProgress,
  useTheme
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSelectedChatbot } from '../context/SelectedChatbotContext';
import { getDemoData } from '../services/api';

const CodeBlock = ({ code, onCopy }) => {
  const theme = useTheme();

  return (
  <Box sx={{ position: 'relative' }}>
    <IconButton
      onClick={onCopy}
      sx={{
        position: 'absolute',
        right: 8,
        top: 8,
        backgroundColor: theme.palette.background.default,
        color: theme.palette.primary.main,
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      <ContentCopyIcon />
    </IconButton>
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        p: 3,
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
        fontFamily: 'Courier New, monospace',
        fontSize: '14px',
        color: theme.palette.text.primary,
        overflowX: 'auto',
        mt: 2,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all'
      }}
    >
      {code}
    </Box>
  </Box>
  );
};

const Implementation = () => {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { selectedChatbot, setSelectedChatbot } = useSelectedChatbot();
  const clientCode = selectedChatbot || 'your-client-code';
  const [demoLinks, setDemoLinks] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDemoLinks = async () => {
      if (!selectedChatbot) {
        // Si no hay chatbot seleccionado, intentar recuperarlo del sessionStorage
        const storedChatbot = sessionStorage.getItem('lastSelectedChatbot');
        if (storedChatbot) {
          setSelectedChatbot(storedChatbot);
          return;
        }
        return;
      }
      
      try {
        setIsLoading(true);
        // Guardar el chatbot actual en sessionStorage antes de obtener los demos
        sessionStorage.setItem('lastSelectedChatbot', selectedChatbot);
        const data = await getDemoData(selectedChatbot);
        setDemoLinks(data);
      } catch (error) {
        console.error('Error al obtener los enlaces de las demos:', error);
        setSnackbar({
          open: true,
          message: 'Error al cargar los enlaces de las demos',
          severity: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDemoLinks();
  }, [selectedChatbot, setSelectedChatbot]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setSnackbar({
        open: true,
        message: 'Código copiado al portapapeles',
        severity: 'success'
      });
    }).catch(() => {
      setSnackbar({
        open: true,
        message: 'Error al copiar el código',
        severity: 'error'
      });
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const codeSnippets = {
    wordpress: `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body>
    <div id="chatbot" data-client="${clientCode}"></div>
    <script src="https://ai4logic.com/chatbot-widget2.js"></script>
</body>
</html>`,
    shopify: `<!-- Añade este código justo antes del cierre de </body> en tu archivo theme.liquid -->
    <div id="chatbot" data-client="${clientCode}"></div>
    <script src="https://ai4logic.com/chatbot-widget2.js"></script>`,
    html: `<!-- Añade este código justo antes del cierre de </body> en tu página HTML -->
    <div id="chatbot" data-client="${clientCode}"></div>
    <script src="https://ai4logic.com/chatbot-widget2.js"></script>`,
    iframe: 
`<iframe 
    width="100%" 
    height="600px" 
    style="border: none; border-radius: 8px; overflow: hidden;" 
    srcdoc='
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="margin: 0; padding: 0;">
        <!-- Chatbot element -->
        <div id="chatbot" data-client="${clientCode}"></div>
        
        <!-- Chatbot script -->
        <script src="https://www.ai4logic.com/chatbot-widget2.js"></script>
    </body>
    </html>
    '
></iframe>`,
    wix: `<!-- Agrega este código en un elemento HTML personalizado en tu sitio Wix -->
<div id="chatbot" data-client="${clientCode}"></div>
<script src="https://ai4logic.com/chatbot-widget2.js"></script>`,
    react: `<!-- Añade este código justo antes del cierre de </body> en tu archivo index.html -->
    <div id="root"></div>
    <div id="chatbot" data-client="${clientCode}"></div>
    <script src="https://ai4logic.com/chatbot-widget2.js"></script>`,
  };

  return (
    <Container maxWidth={false}>
      <Box sx={{ py: 0 }}>      
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3 },
            mb: { xs: 2.5, sm: 3.5 },
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }}
        >
          <Typography variant="h3" component="h1" sx={{ color: theme.palette.text.primary, mb: 0.5, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            Implementación
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '1rem', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            En esta sección puedes acceder a la implementación del asistente. También podrás visualizar las demostraciones.
          </Typography>
        </Paper>

        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            p: 2
          }}
        >
          <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            Código de Implementación (Nueva versión: Widget 2.0)
          </Typography>
          
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            sx={{
              mb: 2,
              borderBottom: 1,
              borderColor: theme.palette.divider,
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main,
              },
              '& .MuiTab-root': {
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <Tab label="WordPress" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }} />
            <Tab label="Shopify" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }} />
            <Tab label="HTML" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }} />
            <Tab label="iFrame" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }} />
            <Tab label="Wix" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }} />
            <Tab label="React" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }} />
          </Tabs>

          {selectedTab === 0 && (
            <>
              <Box sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                <Typography variant="body2" gutterBottom sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  Para WordPress con Elementor, sigue estos pasos:
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  1. Ve a Elementor y añade un nuevo widget "HTML"
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  2. Copia y pega el siguiente código completo en el widget
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  3. Guarda los cambios y publica la página
                </Typography>
              </Box>
              <CodeBlock 
                code={codeSnippets.wordpress} 
                onCopy={() => handleCopyCode(codeSnippets.wordpress)} 
              />

              <Box sx={{ color: theme.palette.text.secondary, mb: 2, mt: 4 }}>
                <Typography variant="body2" gutterBottom sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  WordPress (sin Elementor)
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  1. Entra al panel de administración de WordPress.
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  2. Ve a <b>Apariencia → Editor de archivos del tema</b> (también llamado “Editor de temas” o “Theme File Editor”).
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  3. ⚠️ Si ves un aviso de seguridad, acéptalo sabiendo que vas a modificar código fuente.
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  4. En el panel de la derecha, busca y haz clic en el archivo llamado: <b>footer.php</b> <span style={{fontWeight:400}}>(si tu tema no tiene <b>footer.php</b>, elige <b>header.php</b>, pero es preferible usar <b>footer.php</b>)</span>
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  5. Dentro del código, antes de la etiqueta de cierre <code>&lt;/body&gt;</code>, pega este código:
                </Typography>
                <Box sx={{ mt: 2, mb: 2 }}>
                  <CodeBlock
                    code={`<div id="chatbot" data-client="${clientCode}"></div>\n<script src="https://ai4logic.com/chatbot-widget2.js"></script>`}
                    onCopy={() => handleCopyCode(`<div id="chatbot" data-client="${clientCode}"></div>\n<script src="https://ai4logic.com/chatbot-widget2.js"></script>`)}/>
                </Box>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  6. Haz clic en <b>Actualizar archivo</b>.
                </Typography>
              </Box>
            </>
          )}

          {selectedTab === 1 && (
            <>
              <Box sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                <Typography variant="body2" gutterBottom sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  Para Shopify, sigue estos pasos:
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  1. Ve al panel de administración de tu tienda Shopify
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  2. Navega a Tienda online {'>'} Temas
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  3. Haz clic en "Acciones" {'>'} "Editar código"
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  4. Busca el archivo theme.liquid
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  5. Localiza la etiqueta {'</body>'} y pega el siguiente código justo antes
                </Typography>
              </Box>
              <CodeBlock 
                code={codeSnippets.shopify} 
                onCopy={() => handleCopyCode(codeSnippets.shopify)} 
              />
            </>
          )}

          {selectedTab === 2 && (
            <>
              <Box sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                <Typography variant="body2" gutterBottom sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  Para sitios web HTML, sigue estos pasos:
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  1. Localiza tu archivo public/index.html o el archivo HTML principal de tu sitio
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  2. Abre el archivo con tu editor de código preferido
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  3. Localiza la etiqueta {'</body>'} al final del archivo
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  4. Pega el siguiente código justo antes del cierre del body
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 1, color: theme.palette.warning.main, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  Nota: Si tienes múltiples páginas HTML, deberás añadir este código en cada una de ellas donde quieras que aparezca el chatbot. En caso de tener un Navbar, deberás añadir el código en el archivo del Navbar para aparecer en todas las páginas en toda la aplicación.
                </Typography>
              </Box>
              <CodeBlock 
                code={codeSnippets.html} 
                onCopy={() => handleCopyCode(codeSnippets.html)} 
              />
            </>
          )}
          {selectedTab === 3 && (
            <>
              <Box sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                <Typography variant="body2" gutterBottom sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  Para iFrame, sigue estos pasos:
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  1. Copia el siguiente código
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  2. Pega el código en el archivo HTML para que aparezca el asistente
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  3. Asegúrate de que el archivo HTML esté correctamente estructurado y que el código se inserte en el lugar correcto
                </Typography>
              </Box>
              <CodeBlock 
                code={codeSnippets.iframe} 
                onCopy={() => handleCopyCode(codeSnippets.iframe)} 
              />
            </>
          )}
          {selectedTab === 4 && (
            <>
              <Box sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                <Typography variant="body2" gutterBottom sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  Para Wix, sigue estos pasos:
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  1. En tu editor de Wix, haz clic en el botón "+" para añadir un nuevo elemento
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  2. Busca y selecciona "Elemento HTML" o "HTML personalizado"
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  3. Copia el siguiente código y pégalo en el editor HTML
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  4. Guarda y publica tu sitio para que los cambios sean visibles
                </Typography>
              </Box>
              <CodeBlock 
                code={codeSnippets.wix} 
                onCopy={() => handleCopyCode(codeSnippets.wix)} 
              />
            </>
          )}
          {selectedTab === 5 && (
            <>
              <Box sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                <Typography variant="body2" gutterBottom sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  Para React, sigue estos pasos:
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  1. Localiza la carpeta public
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  2. Copia el siguiente código y pégalo en el archivo index.html dentro de la etiqueta body
                </Typography>
                <CodeBlock 
                  code={codeSnippets.react} 
                  onCopy={() => handleCopyCode(codeSnippets.react)} 
                />
              </Box>
            </>
          )}
        </Paper>

        {/* Nueva sección de Demos */}
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            p: 2,
            mt: 3
          }}
        >
          <Typography variant="h6" sx={{ 
            color: theme.palette.text.primary, 
            mb: 1,
            fontSize: '1.5rem', 
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
          }}>
            Demos
          </Typography>
          <Typography sx={{ 
            color: theme.palette.text.secondary,
            mb: 3,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
          }}>
            En este apartado podrás probar mediante nuestras demos casos reales de cómo quedará el asistente en el cliente
          </Typography>
          <Box sx={{ 
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap'
          }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 2 }}>
                <CircularProgress sx={{ color: theme.palette.primary.main }} />
              </Box>
            ) : demoLinks?.hash ? (
              <>
                <Button
                  variant="contained"
                  href={`https://ai4logic.com/demo1.html?${selectedChatbot};${demoLinks.hash}`}
                  target="_blank"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      borderColor: theme.palette.primary.dark,
                    },
                    height: '40px',
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    width: { xs: '100%', sm: 'auto' },
                    flex: { xs: '1 1 auto', sm: '0 0 auto' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '& .MuiButton-startIcon': {
                      marginRight: 1,
                      '& svg': {
                        fontSize: '1.2rem'
                      }
                    }
                  }}
                >
                  Demo mínima
                </Button>
                <Button
                  variant="contained"
                  href={`https://ai4logic.com/demo2.html?${selectedChatbot};${demoLinks.hash}`}
                  target="_blank"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      borderColor: theme.palette.primary.dark,
                    },
                    height: '40px',
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    width: { xs: '100%', sm: 'auto' },
                    flex: { xs: '1 1 auto', sm: '0 0 auto' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '& .MuiButton-startIcon': {
                      marginRight: 1,
                      '& svg': {
                        fontSize: '1.2rem'
                      }
                    }
                  }}
                >
                  Demo con iframe
                </Button>
                <Button
                  variant="contained"
                  href={`https://ai4logic.com/demo3.html?${selectedChatbot};${demoLinks.hash}`}
                  target="_blank"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      borderColor: theme.palette.primary.dark,
                    },
                    height: '40px',
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    width: { xs: '100%', sm: 'auto' },
                    flex: { xs: '1 1 auto', sm: '0 0 auto' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '& .MuiButton-startIcon': {
                      marginRight: 1,
                      '& svg': {
                        fontSize: '1.2rem'
                      }
                    }
                  }}
                >
                  Demo usando imagen
                </Button>
                <Button
                  variant="contained"
                  href={`https://ai4logic.com/demo4.html?${selectedChatbot};${demoLinks.hash}`}
                  target="_blank"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      borderColor: theme.palette.primary.dark,
                    },
                    height: '40px',
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    width: { xs: '100%', sm: 'auto' },
                    flex: { xs: '1 1 auto', sm: '0 0 auto' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '& .MuiButton-startIcon': {
                      marginRight: 1,
                      '& svg': {
                        fontSize: '1.2rem'
                      }
                    }
                  }}
                >
                  Demo usando web local
                </Button>
              </>
            ) : (
              <Typography sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                No se pudieron cargar los enlaces de las demos
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={3000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          sx={{ 
            backgroundColor: snackbar.severity === 'success' ? theme.palette.success.main : theme.palette.error.main,
            color: snackbar.severity === 'success'
              ? theme.palette.getContrastText(theme.palette.success.main)
              : theme.palette.getContrastText(theme.palette.error.main)
          }}
          onClose={handleCloseSnackbar}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Implementation;
